/**
 * Tiny localStorage JSON cache (works in the browser and the Capacitor WebView).
 *
 * Used to keep the product catalog on the device so reopening the app is
 * instant and costs no network — the single biggest cost/UX win for dealers who
 * open the app dozens of times a day. Never used for PRICES: those are always
 * fetched live so a revoked user loses access immediately and nobody can quote
 * from a stale figure.
 */
const PREFIX = 'mitang.cache.';

export function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function writeCache(key: string, value: unknown): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota/private mode — caching is an optimization, never a requirement.
  }
}

/**
 * Keys currently in the cache that start with `prefix` (prefix stripped).
 * Lets the Saved screen sweep every cached category without having to know
 * which categories exist.
 */
export function cacheKeys(prefix: string): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const raw = localStorage.key(i);
      if (raw?.startsWith(PREFIX + prefix)) out.push(raw.slice(PREFIX.length + prefix.length));
    }
  } catch {
    /* private mode — treat as an empty cache */
  }
  return out;
}

/** True at most once per `windowMs` for the given key (throttles analytics). */
export function throttleGate(key: string, windowMs: number): boolean {
  try {
    const k = `${PREFIX}gate.${key}`;
    const last = Number(localStorage.getItem(k) ?? 0);
    if (Date.now() - last < windowMs) return false;
    localStorage.setItem(k, String(Date.now()));
    return true;
  } catch {
    return true; // can't throttle → don't lose the event
  }
}
